// Учётные записи и роли: один ключ кэша на весь кабинет.
//
// Живёт в общем каталоге, а не внутри раздела «Настройки», потому что список учётных записей
// нужен ДВУМ разделам. В настройках это «кто входит в систему», в «Людях» — вкладка «Доступ» в
// карточке человека и раздача ссылок для входа пачкой из реестра. Второй набор хуков рядом
// означал бы второй ключ кэша на один и тот же ответ: сняли роль в настройках, открыли
// карточку человека — и увидели снятую роль на месте.
//
// Свежесть здесь нулевая намеренно. Кэш переживает перезагрузку окна (`app/query-client.ts`),
// и общие полминуты работают против человека: про доступ «немного устаревшие данные» — это не
// неточность, а прямая ложь. Он решит, что снятие не сработало, и снимет ещё раз.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { RoleOut, UserOut } from '@/shared/api/endpoints/users';
import { listRoles, listUsers } from '@/shared/api/endpoints/users';
import type { ApiError } from '@/shared/errors/api-error';
import type { ListResponse } from '@/shared/api/types';

/** Общий корень: `invalidateQueries({ queryKey: ключиУчётных.все })` гасит и список, и роли. */
export const ключиУчётных = {
  все: ['users'] as const,
  список: (убранные: boolean) => ['users', 'список', { убранные }] as const,
  роли: () => ['users', 'роли'] as const,
};

export function useУчётныеЗаписи(
  включатьУбранных = false,
): UseQueryResult<ListResponse<UserOut>, ApiError> {
  return useQuery({
    queryKey: ключиУчётных.список(включатьУбранных),
    queryFn: ({ signal }) => listUsers({ include_archived: включатьУбранных }, signal),
    staleTime: 0,
  });
}

export function useРоли(): UseQueryResult<RoleOut[], ApiError> {
  return useQuery({
    queryKey: ключиУчётных.роли(),
    queryFn: ({ signal }) => listRoles(signal),
  });
}

/**
 * Учётная запись КОНКРЕТНОГО человека из реестра — или её отсутствие.
 *
 * Возвращает `undefined`, пока список едет, и `null`, когда записи нет. Разница здесь не
 * придирка: «ещё не знаем» и «записи нет» на экране выглядят по-разному, а слипшись, они
 * отправляют человека заводить вторую учётную запись тому, у кого она уже есть.
 */
export function учётнаяЗаписьЧеловека(
  список: ListResponse<UserOut> | undefined,
  employeeId: string,
): UserOut | null | undefined {
  if (список === undefined) {
    return undefined;
  }
  return список.items.find((запись) => запись.employee_id === employeeId) ?? null;
}
